import Breadcrumbs from "@/components/design-system/breadcrumbs";
import { Button } from "@/components/design-system/button";
import { DatePicker } from "@/components/design-system/date-picker";
import { Input } from "@/components/design-system/input";
import SelectWithCheckbox from "@/components/design-system/SelectWithCheckbox";
import SelectWithRadio from "@/components/design-system/SelectWithRadio";
import {
  getGradesDropdownService,
  // getTeachersDropdownService,
} from "@/service/base.service";
import { isAllSchoolsSelected, selectSchoolId, selectUser } from "@/store/slices/authSlice";
import {
  createNewStudent,
  fetchStudentById,
  selectStudentActionLoading,
  updateExistingStudent,
} from "@/store/slices/studentSlice";
import { StudentModel } from "@/types/student.model";
import { successToast } from "@/utils/constants";
import { AppDispatch, RootState } from "@store";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";
import { useParams } from "react-router-dom";

interface AddStudentProps {
  readonly viewMode?: boolean;
  readonly editMode?: boolean;
}

interface StudentFormData {
  uniqueId: string;
  studentName: string;
  dob: string;
  age: string;
  gender: string;
  emailAddress: string;
  contactNumber: string;
  schoolId: string;
  grade: string[];
  teacher: string[];
}

// Field configuration for reusability and scalability
interface FieldConfig {
  id: keyof StudentFormData;
  label: string;
  placeholder: string;
  type?: string;
  required?: boolean;
  isSelect?: boolean;
  min?: number;
  max?: number;
  isMultiple?: boolean;
  isDatePicker?: boolean;
  options?: { value: string; label: string }[];
  readOnly?: boolean;
  maxLength?: number;
}

export default function AddStudent({
  viewMode = false,
  editMode = false,
}: AddStudentProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const actionLoading = useSelector(selectStudentActionLoading);
  const selectedSchoolId = useSelector(selectSchoolId);
  const isAllSchools = useSelector(isAllSchoolsSelected);
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [schoolId, setSchoolId] = useState<any>("");
  const loggedUser = useSelector(selectUser);

  const schoolList: any[] = loggedUser?.school_list?.filter((s) => s.type !== "all") || [];

  const [isLoading, setIsLoading] = useState(viewMode || editMode);
  // const [teachers, setTeachers] = useState<{ id: string; name: string }[]>([]);
  const [grades, setGrades] = useState<{ id: string; grade: string }[]>([]);

  const initialFetchDone = useRef(isAllSchools || false);
  const user = useSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    if (!viewMode && !editMode) {
      const schoolIdFromParams = searchParams.get("schoolId");
      if (schoolIdFromParams) {
        setGrades([]);
        setSchoolId(schoolIdFromParams);
      } else if (selectedSchoolId && !isAllSchools) {
        setGrades([]);
        setSchoolId(selectedSchoolId);
      }
    }
  }, [selectedSchoolId, isAllSchools, viewMode, editMode]);

  useEffect(() => {
    if (schoolId) {
      setValue("schoolId", schoolId);
      fetchGrades(schoolId);
    }
  }, [schoolId]);

  // Initialize react-hook-form
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
    trigger,
  } = useForm<StudentFormData>({
    defaultValues: {
      uniqueId: "",
      studentName: "",
      dob: "",
      age: "",
      gender: "",
      emailAddress: "",
      contactNumber: "",
      schoolId: "",
      grade: [],
      teacher: [],
    },
    mode: "onBlur",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
  });

  // Calculate age from date of birth
  const calculateAge = (dob: string): string => {
    if (!dob) return "";
    const birthDate = moment(dob);
    if (!birthDate.isValid()) return "";
    return moment().diff(birthDate, "years").toString();
  };

  // Update age whenever DOB changes
  useEffect(() => {
    const dob = watch("dob");
    if (dob) {
      setValue("age", calculateAge(dob), { shouldValidate: true });
      // Explicitly trigger validation for age field
    }
  }, [watch("dob"), setValue]);

  useEffect(() => {
    // Only fetch if we haven't done the initial fetch yet
    if (!initialFetchDone.current) {
      fetchGrades();
      // Mark that we've done the initial fetch
      initialFetchDone.current = true;
    }
  }, [dispatch, viewMode]);

  useEffect(() => {
    const watchSchoolId = watch("schoolId");
    if (watchSchoolId) {
      fetchGrades(watchSchoolId);
    }
  }, [watch("schoolId")]);

  const fetchGrades = async (id?: string) => {
    try {
      const { data } = await getGradesDropdownService({
        search: "",
        schoolId: id || schoolId || user?.school_id,
      });
      setGrades(data.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  // Fetch student data when in view or edit mode
  useEffect(() => {
    if ((viewMode || editMode) && id) {
      setIsLoading(true);
      dispatch(fetchStudentById(id))
        .unwrap()
        .then((data) => {
          setIsLoading(false);
          setFormValues(data);
        })
        .catch((error) => {
          console.error("Error fetching student:", error);
          // errorToast("Error", "Failed to load student details");
          setIsLoading(false);
          navigateToBack();
        });
    }
  }, [viewMode, editMode, id]);

  const setFormValues = (studentData: StudentModel) => {
    if (studentData) {
      setValue("uniqueId", studentData.reg_no);
      setValue("studentName", studentData.name);

      // Format the date to YYYY-MM-DD if it exists
      if (studentData.dob) {
        // Check if the date is already in YYYY-MM-DD format
        if (studentData.dob.match(/^\d{4}-\d{2}-\d{2}$/)) {
          setValue("dob", studentData.dob);
        } else if (studentData.dob.match(/^\d{2}-\d{2}-\d{4}$/)) {
          // Convert from DD-MM-YYYY to YYYY-MM-DD
          const [day, month, year] = studentData.dob.split("-");
          setValue("dob", `${year}-${month}-${day}`);
        } else {
          // Convert from any other format to YYYY-MM-DD
          const formattedDate = moment(studentData.dob).format("YYYY-MM-DD");
          setValue("dob", formattedDate);
        }
      } else {
        setValue("dob", "");
      }

      // Age will be calculated automatically from DOB
      setValue("gender", studentData.gender);
      setValue("emailAddress", studentData.email);
      setValue("contactNumber", studentData.contact_number);
      setValue("schoolId", studentData.schoolId || studentData.school_id || "");
      setSchoolId((prevSchoolId: string) => studentData.schoolId || studentData.school_id || "");
      setValue(
        "grade",
        studentData.grade && Array.isArray(studentData.grade)
          ? studentData.grade.map((grade: any) => grade.id)
          : []
      );
      setValue(
        "teacher",
        studentData.teachers
          ? studentData.teachers.map((teacher) => teacher.id)
          : []
      );
    }
  };

  const navigateToBack = () => {
    // navigate(schoolId ? `/user-management/student?schoolId=${schoolId}` : "/user-management/student");
    navigate(-1);
  };

  // Form submission handler
  const onFormSubmit = async (data: StudentFormData) => {
    try {
      // Transform form data to match the StudentModel interface
      // The date should already be in YYYY-MM-DD format from the DatePicker
      let formattedDob = data.dob;
      // If for some reason we still have DD-MM-YYYY format, convert it
      if (data.dob && data.dob.match(/^\d{2}-\d{2}-\d{4}$/)) {
        const [day, month, year] = data.dob.split("-");
        formattedDob = `${year}-${month}-${day}`;
      }

      const studentData: Partial<StudentModel> = {
        name: data.studentName,
        dob: formattedDob,
        age: parseInt(data.age || "0"),
        gender: data.gender,
        email: data.emailAddress,
        contact_number: data.contactNumber,
        grade: data.grade.map((id) => id), // Adding empty grade property to satisfy TypeScript
        teacher_ids: [], // data.teacher.map((id) => id), // Adding empty name property to satisfy TypeScript
        reg_no: data.uniqueId,
        schoolId: data.schoolId || undefined,
      };

      if (editMode && id) {
        // Update existing student
        const resultAction = await dispatch(
          updateExistingStudent({
            id,
            studentData: { ...studentData, reg_no: data.uniqueId },
          })
        );

        if (updateExistingStudent.fulfilled.match(resultAction)) {
          // Show success toast
          successToast(`Student updated successfully.`, "");
          navigateToBack();
        }
      } else {
        // Create new student
        const resultAction = await dispatch(
          createNewStudent(studentData as StudentModel)
        );

        if (createNewStudent.fulfilled.match(resultAction)) {
          // Show success toast
          successToast(`Student added successfully.`, "");
          navigateToBack();
        }
      }
    } catch (error) {
      console.error("Error saving student:", error);
      // errorToast(
      //   "Error",
      //   `Failed to ${editMode ? "update" : "create"} student. Please try again.`
      // );
    }
  };

  // Form validation rules
  const getValidationRules = (field: FieldConfig) => {
    const rules: Record<string, unknown> = {};

    if (field.required) {
      switch (field.id) {
        case "studentName":
          rules.required = "Please enter the student name.";
          break;
        case "dob":
          rules.required = "Please enter the student dob.";
          break;
        case "age":
          rules.required = "Please enter the student age.";
          break;
        case "gender":
          rules.required = "Please enter the student gender.";
          break;
        case "emailAddress":
          rules.required = "Please enter a valid email address.";
          break;
        case "contactNumber":
          rules.required = "Please enter a valid mobile number.";
          break;
        case "grade":
          rules.required = "Please enter a valid grade.";
          break;
        case "schoolId":
          rules.required = "Please select a valid school.";
          break;

        default:
          rules.required = `${field.label} is required`;
      }
    }

    if (field.id === "emailAddress") {
      rules.pattern = {
        value: /^\S+@\S+\.\S+$/,
        message: "Please enter a valid email address",
      };
    }

    if (field.id === "contactNumber") {
      rules.pattern = {
        value: /^[0-9]{10}$/,
        message: "Please enter a valid mobile number",
      };
    }

    if (field.id === "age") {
      rules.min = {
        value: 1,
        message: "Age must be greater than 0",
      };
      rules.max = {
        value: 100,
        message: "Age must be less than 100",
      };
    }

    return rules;
  };

  const handleCancel = () => {
    navigateToBack();
  };

  // Reusable field renderer for consistent UI
  const renderField = (field: FieldConfig) => {
    // Hide uniqueId field in Add mode (when neither viewMode nor editMode is true)
    if (field.id === "uniqueId" && !viewMode && !editMode) {
      return null;
    }

    if (field.isSelect && field.isMultiple) {
      return (
        <>
          <SelectWithCheckbox
            id={field.id}
            label={field.label}
            placeholder={field.placeholder}
            options={field.options || []}
            value={(watch(field.id) as string[]) || []}
            onChange={(value) => {
              setValue(field.id, value as string[], { shouldValidate: true });
              // Manually trigger validation after setting value
              trigger(field.id);
            }}
            showAbove={false}
            error={errors[field.id]?.message as string}
            disabled={viewMode}
            required={field.required}
            maxHeight="300px"
          />
          <input
            type="hidden"
            {...register(field.id, getValidationRules(field))}
          />
        </>
      );
    }

    return (
      <div key={field.id} className="w-full">
        {field.isSelect ? (
          <>
            <SelectWithRadio
              id={field.id}
              label={field.label}
              placeholder={field.placeholder}
              options={field.options || []}
              value={(watch(field.id) as string) || ""}
              onChange={(value) => {
                setValue(field.id, value, { shouldValidate: true });
                if (field.id === "schoolId") {
                  setSchoolId(value);
                  setValue("grade", []);
                }
                // Manually trigger validation after setting value
                trigger(field.id);
              }}
              showAbove={false}
              error={errors[field.id]?.message as string}
              disabled={viewMode}
              required={field.required}
              maxHeight="300px"
            />
            <input
              type="hidden"
              {...register(field.id, getValidationRules(field))}
            />
          </>
        ) : field.isDatePicker ? (
          <>
            <DatePicker
              id={field.id}
              label={field.label}
              placeholder={field.placeholder}
              value={watch(field.id) ? (watch(field.id) as string) : ""}
              onChange={(date) => {
                if (!date) {
                  setValue("age", "");
                }

                setValue(field.id, date, {
                  shouldValidate: true,
                });
                // Manually trigger validation after setting value
                trigger(field.id);
              }}
              maxDate={moment().format("YYYY-MM-DD")}
              error={errors[field.id]?.message as string}
              readOnly={viewMode}
              required={field.required}
              showAbove={false}
            />
            <input
              type="hidden"
              {...register(field.id, getValidationRules(field))}
            />
          </>
        ) : (
          <Input
            id={field.id}
            {...register(field.id, getValidationRules(field))}
            label={field.label}
            type={field.type ?? "text"}
            placeholder={field.placeholder}
            required={field.required}
            errorMessage={errors[field.id]?.message as string}
            min={field.min}
            max={field.max}
            disabled={
              viewMode ||
              (editMode &&
                (field.id === "emailAddress" || field.id === "uniqueId")) ||
              field.readOnly
            }
            readOnly={
              viewMode ||
              (editMode &&
                (field.id === "emailAddress" || field.id === "uniqueId")) ||
              field.readOnly
            }
            maxLength={field.maxLength}
          />
        )}
      </div>
    );
  };

  const studentFields: FieldConfig[] | any[] = [
    {
      id: "uniqueId",
      label: "Unique ID",
      placeholder: "Enter Unique ID",
      required: false,
      readOnly: true,
    },
    {
      id: "studentName",
      label: "Student Name",
      placeholder: "Enter Student Name",
      required: true,
      maxLength: 60,
    },
    {
      id: "dob",
      label: "DOB",
      placeholder: "Enter Date of Birth",
      isDatePicker: true,
      required: true,
    },
    {
      id: "age",
      label: "Age",
      placeholder: "Age",
      // type: "number",
      min: 1,
      max: 100,
      required: true,
      readOnly: true,
    },
    {
      id: "gender",
      label: "Gender",
      placeholder: "Select Gender",
      isSelect: true,
      required: true,
      options: [
        { value: "male", label: "Male" },
        { value: "female", label: "Female" },
        { value: "other", label: "Other" },
      ],
    },
    {
      id: "emailAddress",
      label: "Email Address",
      placeholder: "Enter Email Address",
      type: "email",
      required: true,
    },
    {
      id: "contactNumber",
      label: "Contact Number",
      placeholder: "Your Contact Number",
      type: "tel",
      required: true,
    },
    ...(isAllSchools
      ? [
        {
          id: "schoolId",
          label: "School",
          placeholder: "Select School",
          isSelect: true,
          required: true,
          isMultiple: false,
          options: schoolList?.map((school) => ({
            value: school.id,
            label: school.name,
          })),
        },
      ]
      : []),
    {
      id: "grade",
      label: "Grade",
      placeholder: "Select Grade",
      isSelect: true,
      required: true,
      isMultiple: true,
      options: grades.map((grade) => ({ value: grade.id, label: grade.grade })),
    },
    // {
    //   id: "teacher",
    //   label: "Teacher",
    //   placeholder: "Select Teacher",
    //   isSelect: true,
    //   isMultiple: true,
    //   required: false,
    //   options: teachers.map((teacher) => ({
    //     value: teacher.id,
    //     label: teacher.name,
    //   })),
    // },
  ];

  return (
    <div className="h-full flex flex-col gap-6">
      {isLoading ? (
        <div className="bg-white rounded-[12px] h-[calc(100vh-120px)] flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-t-[#5C88C5] border-r-[#5C88C5] border-b-[#5C88C5] border-l-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">Loading school details...</p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-[12px] h-full">
          <form
            onSubmit={handleSubmit(onFormSubmit)}
            className="w-full h-full flex flex-col"
          >
            {/* Student Details Section */}
            <div className={`px-[32px] py-[33px] flex flex-col flex-1`}>
              <div className="mb-8">
                <div className="">
                  <Breadcrumbs
                    title={
                      viewMode
                        ? "View Student"
                        : editMode
                          ? "Edit Student"
                          : "Add Student"
                    }
                    onClick={() => navigateToBack()}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {studentFields.map(renderField)}
                </div>
              </div>
            </div>
            {/* Form Actions */}
            <div className="flex h-[98px] border px-[32px] justify-end space-x-4 border-t border-gray-200 pt-6 shadow-[0px_4px_24px_8px_#0000001F]">
              {viewMode ? (
                <Button
                  id="view-student-close"
                  type="button"
                  className="h-[48px] w-[161px] rounded-[8px] bg-[#5C88C5]"
                  variant="primary"
                  onClick={handleCancel}
                >
                  Close
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    className="h-[48px] w-[161px]"
                    variant="primary-outline"
                    onClick={handleCancel}
                    disabled={actionLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="h-[48px] w-[161px] rounded-[8px] bg-[#5C88C5]"
                    variant="primary"
                    disabled={!isValid || actionLoading}
                    loading={actionLoading}
                  >
                    {editMode ? "Update" : "Add"}
                  </Button>
                </>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
